// GH-766 — company "באנר" tab: the company's EXISTING private-banner + lottery-popup fields in one place
// (moved out of "מידע כללי"). No new fields; saved with the regular company Save button.
// Images upload at original quality (setting/banner-image) and the field stores the CDN URL.
import { Box, Grid, Typography } from '@mui/material';
import { BooleanInput, TextInput, useInput } from 'react-admin';
import BannerImagePicker from '../Banner/BannerImagePicker';

const BannerImageInput = ({ source, label, hint }: { source: string; label: string; hint?: string }) => {
	const { field } = useInput({ source });
	// legacy shape guard: an un-saved react-admin ImageInput value is an object { src, rawFile }
	const value = typeof field.value === 'string' ? field.value : field.value?.src || '';
	return <BannerImagePicker label={label} hint={hint} value={value} onChange={(url) => field.onChange(url)} />;
};

const jsonFormat = (v: any) => (v === undefined || v === null || v === '' ? '' : typeof v === 'string' ? v : JSON.stringify(v));
// '' → null so clearing the field is really saved (undefined would be dropped from the PUT body);
// valid JSON → object; anything else is kept as typed (the field is Mixed — never block saving the company).
const jsonParse = (v: string) => {
	if (!v || !v.trim()) return null;
	try {
		return JSON.parse(v);
	} catch {
		return v;
	}
};

const Section = ({ title, note }: { title: string; note?: string }) => (
	<Box sx={{ mt: 2, mb: 1 }}>
		<Typography variant="h6">{title}</Typography>
		{note && (
			<Typography variant="caption" color="textSecondary">
				{note}
			</Typography>
		)}
	</Box>
);

const CompanyBannerTab = () => (
	<Box sx={{ width: '100%', maxWidth: 900 }}>
		<Section title="באנר פרטי" note='כשהמתג דלוק, עובדי החברה רואים את הבאנר הזה במקום הבאנר הראשי.' />
		<BooleanInput label="הצג באנר פרטי" source="show_private_banner" />
		<BannerImageInput source="banner_url" label="תמונה בסיסית" hint="האפליקציה והאתר בטלפון" />
		<BannerImageInput source="banner_url_desktop" label="תמונה לאתר במחשב" />
		<BannerImageInput source="banner_url_en" label="תמונה באנגלית" />

		<Section title="לחיצה על הבאנר" />
		<BooleanInput label="לחיצה על הבאנר מנווטת" source="banner_navigate" />
		<Grid container spacing={2}>
			<Grid item xs={12} sm={7}>
				<TextInput source="banner_navigate_name" label="לאן מנווטים (מסך באפליקציה או קישור)" fullWidth />
			</Grid>
			<Grid item xs={12} sm={5}>
				<TextInput
					source="banner_navigate_params"
					label="פרמטרים למסך (JSON)"
					fullWidth
					format={jsonFormat}
					parse={jsonParse}
				/>
			</Grid>
		</Grid>

		<Section title="פופאפ הגרלה" note="נפתח בלחיצה על הבאנר רק כשהניווט כבוי והפופאפ דלוק." />
		<BooleanInput label="הצג פופאפ הגרלה" source="show_banner_popup" />
		<BannerImageInput source="main_banner_image_popup" label="פופאפ כתמונה אחת" hint="אם מלא — מוצגת רק התמונה, בלי כותרות וכפתור" />
		<BannerImageInput source="banner_popup_image" label="תמונה בראש הפופאפ" />
		<Grid container spacing={2}>
			<Grid item xs={12} sm={6}>
				<TextInput source="banner_popup_title1" label="כותרת — שורה 1" fullWidth />
			</Grid>
			<Grid item xs={12} sm={6}>
				<TextInput source="banner_popup_title2" label="כותרת — שורה 2" fullWidth />
			</Grid>
		</Grid>
		<TextInput source="banner_popup_text" label="טקסט ההסבר" fullWidth multiline minRows={3} />
		<TextInput source="banner_popup_btn_text" label="טקסט הכפתור" fullWidth />
		<TextInput source="banner_popup_info" label="אותיות קטנות" fullWidth multiline minRows={2} />
		<TextInput source="banner_text_share" label="טקסט השיתוף בוואטסאפ (הקישור נוסף אחריו)" fullWidth multiline minRows={3} />
	</Box>
);

export default CompanyBannerTab;
